import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const InvitationHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [action, setAction] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    const urlPath = window.location.pathname;
    
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Determine if this is accept or decline
    const isAccept = urlPath.includes('/accept');
    setAction(isAccept ? 'accept' : 'decline');

    // If user is not logged in and trying to accept, redirect to auth with return URL
    if (!user && isAccept) {
      localStorage.setItem('invitation_token', token);
      navigate('/auth?action=signup&redirect=invitation');
      return;
    }

    // Process the invitation
    handleInvitation(token, isAccept);
  }, [searchParams, user, navigate]);

  const handleInvitation = async (token, isAccept) => {
    try {
      // First, get invitation details
      const detailsResponse = await fetch(`${API_BASE_URL}/api/invitations/token/${token}`, {
        credentials: 'include',
      });

      if (!detailsResponse.ok) {
        const errorData = await detailsResponse.json();
        throw new Error(errorData.detail || 'Invalid or expired invitation');
      }

      const invitation = await detailsResponse.json();

      // Process accept or decline
      const endpoint = isAccept 
        ? `${API_BASE_URL}/api/invitations/${invitation._id}/accept`
        : `${API_BASE_URL}/api/invitations/${invitation._id}/reject`;

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to process invitation');
      }

      const data = await response.json();
      
      if (isAccept) {
        setMessage(`Successfully joined ${invitation.workspace_name}! Redirecting to dashboard...`);
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setMessage('Invitation declined. Redirecting...');
        setTimeout(() => navigate(user ? '/dashboard' : '/'), 2000);
      }
    } catch (err) {
      console.error('Invitation error:', err);
      setError(err.message || 'Failed to process invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center">
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Processing {action === 'accept' ? 'Acceptance' : 'Decline'}...
              </h2>
              <p className="text-gray-600">Please wait while we process your invitation.</p>
            </>
          ) : error ? (
            <>
              <div className="bg-red-100 text-red-600 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => navigate(user ? '/dashboard' : '/')}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition"
              >
                Go to {user ? 'Dashboard' : 'Home'}
              </button>
            </>
          ) : (
            <>
              <div className="bg-green-100 text-green-600 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Success!</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitationHandler;
